import { useContext, useState } from "react"
import { Div, Text } from "atomize"
import { toast } from "react-toastify"
import { faRightFromBracket } from "@fortawesome/free-solid-svg-icons"
import ConfirmButtons from "../buttons/ConfirmButtons"
import Container from "../atoms/Container"
import { theme } from "../settings/config"
import { callFunction } from "../services/firebase.service"
import { schoolContext } from "../providers/school.provider"
import { profileContext } from "../providers/profile.provider"
import { notebookContext } from "../providers/notebook.provider"
import { Option } from "../notebook/NotebookChanges"
import { ARCHIVED_SCHOOL, activeSchoolIDs } from "../utils/school.utils"
import { isMine, isSharedIn } from "../utils/notebook.utils"

// Andarsene è una decisione propria e non ha bisogno del permesso di
// nessuno. Le lezioni che hai segnato restano al circolo: sono successe, e
// ci sono attaccati i pagamenti di allievi che ci vanno ancora.
//
// Chi lì non lavora più ma vuole tenersi lo storico non deve uscire: lo
// archivia, e gli resta tutto da riguardare. Glielo si dice qui, dove sta
// per perderlo — se c'è un altro circolo in uso, perché l'ultimo non si
// archivia.
//
// Chi nel quaderno ha cose condivise coi colleghi di qui sceglie: portarle
// via (restano sue, e lì non le vede più nessuno) o lasciarne una copia al
// circolo, che tiene chi lo dirige.
export function LeaveSchoolModal({ closeModal }) {
	const { coach } = useContext(profileContext)
	const { schools, selectedSchool, getSelectedSchool, setSchoolPlacement } =
		useContext(schoolContext)
	const { entries } = useContext(notebookContext) ?? {}
	const [isSaving, setIsSaving] = useState(false)
	const [leavesCopies, setLeavesCopies] = useState(false)

	const sharedHere = (entries ?? []).filter(
		entry => isMine(entry) && isSharedIn(entry, selectedSchool),
	).length
	const school = getSelectedSchool()
	const ownerName =
		school?.coaches && !Array.isArray(school.coaches)
			? (school.coaches[school.ownerCoachID]?.coachName ?? "chi dirige il circolo")
			: "chi dirige il circolo"

	const schoolName = getSelectedSchool()?.schoolName ?? "questo circolo"
	const canArchive = activeSchoolIDs(coach, Object.keys(schools ?? {})).some(
		id => id !== selectedSchool,
	)

	const handleArchive = async () => {
		setIsSaving(true)
		try {
			await setSchoolPlacement(selectedSchool, ARCHIVED_SCHOOL)
			toast.success(`${schoolName} archiviato: lo storico lo ritrovi in "I tuoi circoli"`, {
				position: "top-center",
			})
			closeModal()
		} catch (err) {
			toast.error(err.message || "Non sono riuscito ad archiviarlo", {
				position: "top-center",
			})
			setIsSaving(false)
		}
	}

	const handleLeave = async () => {
		setIsSaving(true)
		try {
			const leave = callFunction("leaveSchool")
			await leave({ schoolID: selectedSchool, leaveNotebookCopies: sharedHere > 0 && leavesCopies })
			toast.success(`Sei uscito da ${schoolName}`, { position: "top-center" })
			closeModal()
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a farti uscire", {
				position: "top-center",
			})
			setIsSaving(false)
		}
	}

	return (
		<Container>
			<Div m={{ b: "0.5rem" }} textWeight="bold" textSize="title">
				Uscire da {schoolName}?
			</Div>
			<Text textColor={theme.colors.gunmetal}>
				Da subito non vedrai più gli allievi né le lezioni di qui. Quelle che hai segnato
				restano al circolo — sono successe, e ci sono attaccati i pagamenti di allievi che
				ci vanno ancora. Se un giorno ti rifanno entrare, ritrovi tutto.
			</Text>
			{canArchive && (
				<Text textColor={theme.colors.gunmetal} m={{ t: "0.75rem" }}>
					Se lì non lavori più ma vuoi poter riguardare lezioni e conti,{" "}
					<Text
						tag="span"
						textWeight="bold"
						textColor={theme.colors.primary}
						cursor={isSaving ? "default" : "pointer"}
						onClick={() => !isSaving && handleArchive()}>
						archivialo invece
					</Text>
					: sparisce dal selettore e lo storico resta.
				</Text>
			)}
			{sharedHere > 0 && (
				<>
					<Text textWeight="bold" textColor={theme.colors.text} m={{ t: "1rem", b: "0.5rem" }}>
						{sharedHere === 1
							? "Nel quaderno hai una cosa condivisa con i colleghi di qui:"
							: `Nel quaderno hai ${sharedHere} cose condivise con i colleghi di qui:`}
					</Text>
					<Div role="radiogroup" d="flex" flexDir="column" style={{ gap: "0.5rem" }}>
						<Option
							isSelected={!leavesCopies}
							title="Portale via con te"
							text="Restano nel tuo quaderno, e qui non le vede più nessuno."
							onSelect={() => setLeavesCopies(false)}
						/>
						<Option
							isSelected={leavesCopies}
							title="Lasciane una copia al circolo"
							text={`La tiene ${ownerName}, che dirige il circolo, e i colleghi continuano a usarla. Nel tuo quaderno restano le tue.`}
							onSelect={() => setLeavesCopies(true)}
						/>
					</Div>
				</>
			)}
			<ConfirmButtons
				confirmText="Sì, esco"
				icon={faRightFromBracket}
				busy={isSaving}
				busyText="Esco..."
				onConfirm={handleLeave}
				cancelText="No, resto"
				onCancel={closeModal}
			/>
		</Container>
	)
}

export default LeaveSchoolModal
